import { AxiosResponse } from 'axios';

import apiClient from './client';
import i18n from 'i18n';
import { ApiResponseDto } from 'interfaces/apiResponse';
import { Branch } from 'interfaces/branch';
import { StructuredAddress } from 'interfaces/structuredAddress';

export interface BranchCreateRequest {
  name: string;
  address: StructuredAddress;
  companyId: number | null;
  aadeBranchCode: number | null;
}

export interface BranchUpdateRequest {
  name: string;
  address: StructuredAddress;
  enabled: boolean;
  companyId: number | null;
  aadeBranchCode: number | null;
}

interface BranchesService {
  getBranches(enabled?: boolean): Promise<Branch[]>;
  createBranch(data: BranchCreateRequest): Promise<Branch>;
  updateBranch(id: number, data: BranchUpdateRequest): Promise<Branch>;
  deleteBranch(id: number): Promise<void>;
}

const endpoints = {
  branches: '/branches',
  branch: (id: number) => `/branches/${id}`,
};

/**
 * Translates a backend error code into a localised message, following the apiErrors convention. Falls
 * back to the backend's own message when the code is not one we have a translation for.
 */
const translateBranchError = (error: any): Error | null => {
  const firstError = error.response?.data?.errors?.[0];
  if (!firstError) {
    return null;
  }

  if (firstError.code === 'ACTIVE_BRANCH_LIMIT_REACHED' && firstError.details) {
    const { branchLimit } = firstError.details;
    return new Error(i18n.t('apiErrors.activeBranchLimitReached', { branchLimit }));
  }

  if (firstError.code === 'BRANCH_NAME_ALREADY_EXISTS') {
    return new Error(i18n.t('apiErrors.branchNameAlreadyExists', { branchName: firstError.details?.branchName ?? '' }));
  }

  if (firstError.code === 'ADDRESS_INCOMPLETE') {
    return new Error(i18n.t('apiErrors.addressIncomplete', { missingComponents: firstError.details?.missingComponents ?? '' }));
  }

  return new Error(firstError.message);
};

export const branchesService: BranchesService = {
  getBranches: async (enabled?: boolean): Promise<Branch[]> => {
    const response: AxiosResponse<ApiResponseDto<Branch[]>> = await apiClient.get(endpoints.branches, {
      params: { enabled },
    });

    if (response.data.errors) {
      throw new Error(response.data.errors!.map(error => error.message).join(', '));
    }

    return response.data.data!;
  },

  createBranch: async (data: BranchCreateRequest): Promise<Branch> => {
    try {
      const response: AxiosResponse<ApiResponseDto<Branch>> = await apiClient.post(endpoints.branches, data);

      if (response.data.errors) {
        throw new Error(response.data.errors!.map(error => error.message).join(', '));
      }

      return response.data.data!;
    } catch (error: any) {
      const translated = translateBranchError(error);
      if (translated) {
        throw translated;
      }

      throw error;
    }
  },

  updateBranch: async (id: number, data: BranchUpdateRequest): Promise<Branch> => {
    try {
      const response: AxiosResponse<ApiResponseDto<Branch>> = await apiClient.put(endpoints.branch(id), data);

      if (response.data.errors) {
        throw new Error(response.data.errors!.map(error => error.message).join(', '));
      }

      return response.data.data!;
    } catch (error: any) {
      const translated = translateBranchError(error);
      if (translated) {
        throw translated;
      }

      throw error;
    }
  },

  deleteBranch: async (id: number): Promise<void> => {
    await apiClient.delete(endpoints.branch(id));
  },
};
